import { TICKET_CREATE_SUGGEST_LIMIT, type KbSuggestQuery } from '@gb-ticket/shared-types';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { CheckIcon, Loader2Icon } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useMarkKbHelpful, useKbSuggest } from '@/products/helpdesk/features/kb/use-kb';
import { useAppPaths } from '@/products/helpdesk/features/portal/portal-paths';
import { StatusBadge } from '@/products/helpdesk/features/tickets/ticket-badges';
import { ApiRequestError } from '@/lib/api';

export function TicketCreateSuggestions({
  q,
  categoryId,
}: {
  q: string | null;
  categoryId?: string;
}) {
  const copy = useVoiceCopy();
  const navigate = useNavigate();
  const paths = useAppPaths();
  const query: KbSuggestQuery | null = q
    ? { q, limit: TICKET_CREATE_SUGGEST_LIMIT, categoryId }
    : null;
  const suggest = useKbSuggest(query);
  const markHelpful = useMarkKbHelpful();

  if (!q) {
    return (
      <Card className="lg:sticky lg:top-20">
        <CardHeader>
          <CardTitle>{copy('helpdesk.ticket.suggest.title')}</CardTitle>
          <CardDescription>
            Sobald Betreff oder Beschreibung konkret genug sind, erscheinen hier passende Artikel
            und gelöste Tickets.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const articles = suggest.data?.articles ?? [];
  const tickets = suggest.data?.tickets ?? [];
  const empty = !suggest.isPending && articles.length === 0 && tickets.length === 0;

  async function onSolvedByArticle(slug: string) {
    try {
      const result = await markHelpful.mutateAsync(slug);
      toast.success(`„${result.title}“ hat geholfen. Es wurde kein Ticket erstellt.`);
      void navigate(paths.article(result.slug));
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Rückmeldung konnte nicht gespeichert werden.',
      );
    }
  }

  function onSolvedByTicket() {
    toast.success('Schön, dass die bestehende Lösung geholfen hat. Es wurde kein Ticket erstellt.');
    void navigate(paths.home);
  }

  return (
    <Card className="lg:sticky lg:top-20">
      <CardHeader>
        <CardTitle>{copy('helpdesk.ticket.suggest.title')}</CardTitle>
        <CardDescription>{copy('helpdesk.ticket.suggest.skip')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {suggest.isFetching && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" aria-hidden />
            Passende Lösungen werden gesucht …
          </p>
        )}
        {empty && (
          <p className="text-sm text-muted-foreground">
            Keine passenden Artikel oder gelösten Tickets gefunden.
          </p>
        )}

        {articles.length > 0 && (
          <ul className="space-y-3">
            {articles.map((hit) => (
              <li key={hit.article.id} className="space-y-2 rounded-md border p-3">
                <Link
                  to={paths.article(hit.article.slug)}
                  className="font-medium hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  {hit.article.title}
                </Link>
                {hit.snippet && <p className="text-sm text-muted-foreground">{hit.snippet}</p>}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={markHelpful.isPending}
                  onClick={() => void onSolvedByArticle(hit.article.slug)}
                >
                  <CheckIcon />
                  Das hat mein Problem gelöst
                </Button>
              </li>
            ))}
          </ul>
        )}

        {tickets.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
              Ähnliche gelöste Tickets
            </p>
            <ul className="space-y-3">
              {tickets.map((ticket) => (
                <li key={ticket.id} className="space-y-2 rounded-md border p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link to={paths.ticket(ticket.number)} className="font-medium hover:underline">
                      #{ticket.number} · {ticket.subject}
                    </Link>
                    <StatusBadge status={ticket.status} />
                  </div>
                  {ticket.snippet && (
                    <p className="text-sm text-muted-foreground">{ticket.snippet}</p>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => void onSolvedByTicket()}
                  >
                    <CheckIcon />
                    Das hat mein Problem gelöst
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
